import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { classNames } from '@/utils/classNames';
import { generateAriaProps } from '@/utils/accessibility';

interface DriverButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  size?: 'large' | 'extra-large';
  loading?: boolean;
  icon?: React.ReactNode;
  badge?: string | number;
  urgent?: boolean;
  children: React.ReactNode;
}

/**
 * ドライバー向け大型ボタンコンポーネント
 *
 * 陸送業界の特殊要件に対応：
 * - 低リテラシー対応（大きなテキスト、明確な色分け）
 * - タッチデバイス対応（大きなタッチターゲット）
 * - 屋外・車内での視認性確保（高コントラスト）
 * - 緊急時の視覚的強調（点滅、大きな警告色）
 */
const DriverButton = forwardRef<HTMLButtonElement, DriverButtonProps>(
  (
    {
      variant = 'primary',
      size = 'large',
      loading = false,
      disabled,
      urgent = false,
      icon,
      badge,
      className,
      children,
      'aria-label': ariaLabel,
      ...props
    },
    ref
  ) => {
    const baseClasses = [
      'relative inline-flex items-center justify-center',
      'font-bold text-center leading-tight',
      'border-2 rounded-xl',
      'transition-all duration-200 ease-in-out',
      'focus:outline-none focus:ring-4 focus:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-60',
      'touch-target-large', // アクセシビリティ用クラス
      urgent && !disabled && 'animate-pulse', // 緊急時の点滅
      loading && 'cursor-wait',
    ]
      .filter(Boolean)
      .join(' ');

    const variantClasses = {
      primary: [
        'bg-primary-600 text-white border-primary-600',
        'hover:bg-primary-700 hover:border-primary-700',
        'active:bg-primary-800 active:border-primary-800',
        'focus:ring-primary-500',
        'dark:bg-primary-600 dark:hover:bg-primary-700',
        'disabled:bg-gray-400 disabled:border-gray-400 disabled:text-gray-200',
      ].join(' '),
      secondary: [
        'bg-gray-200 text-gray-900 border-gray-300',
        'hover:bg-gray-300 hover:border-gray-400',
        'active:bg-gray-400 active:border-gray-500',
        'focus:ring-gray-500',
        'dark:bg-gray-700 dark:text-white dark:border-gray-600',
        'dark:hover:bg-gray-600 dark:hover:border-gray-500',
        'disabled:bg-gray-100 disabled:border-gray-200 disabled:text-gray-400',
      ].join(' '),
      danger: [
        'bg-red-600 text-white border-red-600',
        'hover:bg-red-700 hover:border-red-700',
        'active:bg-red-800 active:border-red-800',
        'focus:ring-red-500',
        'dark:bg-red-600 dark:hover:bg-red-700',
        'disabled:bg-gray-400 disabled:border-gray-400 disabled:text-gray-200',
      ].join(' '),
      success: [
        'bg-green-600 text-white border-green-600',
        'hover:bg-green-700 hover:border-green-700',
        'active:bg-green-800 active:border-green-800',
        'focus:ring-green-500',
        'dark:bg-green-600 dark:hover:bg-green-700',
        'disabled:bg-gray-400 disabled:border-gray-400 disabled:text-gray-200',
      ].join(' '),
    };

    const sizeClasses = {
      large: [
        'min-h-[60px] px-8 py-4',
        'text-lg', // 18px
        'min-w-[120px]',
      ].join(' '),
      'extra-large': [
        'min-h-[80px] px-12 py-6',
        'text-xl', // 20px
        'min-w-[160px]',
      ].join(' '),
    };

    // ARIA属性の生成
    const ariaProps = generateAriaProps({
      label: ariaLabel,
      disabled: disabled || loading,
      pressed: props['aria-pressed'] === 'true' || props['aria-pressed'] === true,
    });

    // キーボード操作ハンドラー
    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled || loading) return;

      // Enter または Space キーでクリックを実行
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        // キーボードイベントをマウスイベントとして扱う
        const syntheticEvent = {
          type: 'click',
          currentTarget: event.currentTarget,
          target: event.target,
          button: 0,
          buttons: 1,
          clientX: 0,
          clientY: 0,
          pageX: 0,
          pageY: 0,
          screenX: 0,
          screenY: 0,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
          altKey: event.altKey,
          metaKey: event.metaKey,
          detail: 1,
          bubbles: true,
          cancelable: true,
          defaultPrevented: false,
          eventPhase: Event.BUBBLING_PHASE,
          isTrusted: true,
          timeStamp: event.timeStamp,
          nativeEvent: {} as MouseEvent,
          preventDefault: event.preventDefault.bind(event),
          stopPropagation: event.stopPropagation.bind(event),
          isPropagationStopped: () => false,
          isDefaultPrevented: () => false,
        } as React.MouseEvent<HTMLButtonElement, MouseEvent>;
        props.onClick?.(syntheticEvent);
      }

      // 元のonKeyDownイベントも実行
      props.onKeyDown?.(event);
    };

    return (
      <button
        ref={ref}
        className={classNames(baseClasses, variantClasses[variant], sizeClasses[size], className)}
        disabled={disabled || loading}
        {...ariaProps}
        {...props}
        onKeyDown={handleKeyDown}
      >
        {/* バッジ表示（通知数など） */}
        {badge && (
          <span
            className='absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center'
            aria-label={`${badge}件の通知`}
          >
            {badge}
          </span>
        )}

        <span className='flex items-center justify-center gap-3'>
          {/* ローディングスピナー */}
          {loading && (
            <svg
              className='animate-spin h-6 w-6'
              fill='none'
              viewBox='0 0 24 24'
              aria-hidden='true'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              />
            </svg>
          )}

          {/* アイコン表示 */}
          {!loading && icon && (
            <span className='flex-shrink-0' aria-hidden='true'>
              {icon}
            </span>
          )}

          {/* ボタンテキスト */}
          <span className='font-bold'>{children}</span>
        </span>

        {/* 緊急時の視覚的インジケーター */}
        {urgent && !disabled && (
          <span
            className='absolute inset-0 rounded-xl border-2 border-yellow-400 animate-ping'
            aria-hidden='true'
          />
        )}
      </button>
    );
  }
);

DriverButton.displayName = 'DriverButton';

export default DriverButton;
